import styled from 'styled-components'
import { useAtom } from 'jotai'

import { rwProfileMetadataAtom } from '../../../atoms/profile'
import { ProfileEditDialog } from '../edit'
import { ProfileMetadata } from '../../../interfaces'
import { GradientBanner } from '../'
import { FollowChip } from '../follow'
import FollowDialog from '../follow/FollowDialog'
import SocialContainer from './SocialContainer'
import { DescExpand, Description, useShowExpand } from '../../video/details/VideoDetails/common'
import React, { useState } from 'react'
import { rwStreamClientAtom } from '../../../atoms/StreamClient'
import ProfileImage from '../image'
import { getShortenedAddress } from '../../../utils/address'
import useAuth from '../../../hooks/auth'
import { fbGetUserMetadata } from '../../../data/FirebaseEnrich'
import { useMediaQuery } from '@material-ui/core'
import { useQuery } from '@apollo/client'
import { USER_STATS_QUERY } from '../../../graphql/graphql-queries'
import { Query_Root } from '../../../graphql/indexer-graphql-types'

// ========================== Styled components ================================

const BannerGradientWrapper = styled.div`
  z-index: 1;
  width: 100%;
  height: 250px;
  position: absolute;
  top: 100px;

  @media (min-width: 43em) {
    top: 100px;
  }
`

const BannerImage = styled.img`
  z-index: 1;
  width: 100%;
  object-fit: cover;
  height: 250px;
  position: absolute;
  top: 100px;

  @media (min-width: 43em) {
    top: 100px;
  }
`

const BannerShadow = styled.div`
  width: 100%;
  height: 250px;
  z-index: 2;
  position: absolute;
  background: linear-gradient(180deg, rgba(16, 16, 16, 0) 0%, #101010 100%);
  top: 100px;

  @media (min-width: 43em) {
    top: 100px;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1320px;
  position: relative;
  margin: 0 auto;
`

const ImageSpacer = styled.div`
  height: 200px;

  // mobile
  @media (max-width: 43em) {
    height: 112px;
  }
`

const OuterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 15px;
  margin-left: 12px;
  margin-right: 12px;
  width: calc(100% - 24px);
  z-index: 4;

  @media (min-width: 43em) {
    width: calc(100% - 50px);
    margin: 0;
    margin-left: 25px;
    margin-right: 25px;
  }
`

const ProfileDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  z-index: 4;

  @media (max-width: 43em) {
    flex-direction: column;
  }
`

const ProfileImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-right: 25px;
`

const MainInfo = styled.div`x
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  flex: 1;
`

const NameTitle = styled.div`
  font-size: 44px;
  font-weight: 700;
  text-align: left;
  background: -webkit-linear-gradient(0deg, #fff, #fff, #b1b1b1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  // tablet
  @media (max-width: 62em) {
    font-size: 40px;
  }

  // mobile
  @media (max-width: 43em) {
    font-size: 28px;
    margin-top: 15px;
  }
`

const StatsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin-left: 25px;

  @media (max-width: 43em) {
    gap: 0;
  }
`

const StatDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex=start;
  gap: 10px;

  @media (max-width: 62em) {
    flex-direction: row;
    align-items: center;
  }

  @media (max-width: 43em) {
    gap: 5px;
  }
`

const StatTitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  color: #cdcdcd;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  text-align: left;

  @media (max-width: 43em) {
    font-size: 18px;
  }
`

const FollowDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 25px;
  align-items: center;

  @media (max-width: 43em) {
    width: 100%;
    gap: 0;
    justify-content: space-between;
  }
`

// ================================= Types =====================================

type ProfileHeaderProps = {
  address: string
  metadata?: ProfileMetadata
}

// ========================== React component ==================================

const ProfileHeader = ({ address, metadata }: ProfileHeaderProps): JSX.Element => {
  const [expanded, expandDesc] = React.useState(false)
  const [isDivLoaded, setIsDivLoaded] = React.useState(false)
  const isMobile = useMediaQuery('(max-width: 43em)')
  const isTablet = useMediaQuery('(max-width: 62em)')
  const showExpand = useShowExpand(isDivLoaded, 2)
  const { profile } = useAuth()
  const [updatedMetadata, setInitialMetadata] = useAtom(rwProfileMetadataAtom)
  const [streamClient] = useAtom(rwStreamClientAtom)
  const notificationFeed = streamClient.feed('notifications', address)
  const videoFeed = streamClient.feed('videos', address)
  const [followingCount, setFollowingCount] = useState<number | undefined>()
  const [followersCount, setFollowersCount] = useState<number | undefined>()
  const statsData = useQuery<Query_Root>(USER_STATS_QUERY, { variables: { user: address } })?.data

  const collectors = statsData?.tokenAccountAggregate.aggregate?.count ?? 0
  const collectedFrom = statsData?.editionMetadataAggregate.aggregate?.count ?? 0
  const isMyProfile = profile?.address === address
  const bannerImageExists =
    (isMyProfile && updatedMetadata?.banner_image_uri !== undefined) || metadata?.banner_image_uri
  const youtubeHandle =
    isMyProfile && updatedMetadata?.youtube_link !== undefined
      ? updatedMetadata.youtube_link
      : metadata?.youtube_link
  const instagramHandle =
    isMyProfile && updatedMetadata?.instagram_link !== undefined
      ? updatedMetadata.instagram_link
      : metadata?.instagram_link
  const twitterHandle =
    isMyProfile && updatedMetadata?.twitter_link !== undefined
      ? updatedMetadata?.twitter_link
      : metadata?.twitter_link
  const profileImageUri =
    isMyProfile && updatedMetadata?.profile_image_uri !== undefined
      ? updatedMetadata.profile_image_uri
      : metadata?.profile_image_uri
  React.useEffect(() => {
    setIsDivLoaded(true)
  }, [])

  const refetchProfileData = async () => {
    const metadata = await fbGetUserMetadata(address)
    setInitialMetadata(metadata as ProfileMetadata)
  }

  React.useEffect(() => {
    if (!address) {
      return
    }

    ;(async () => {
      const [notificationFeedStats, videoFeedStats] = await Promise.all([
        notificationFeed.followStats(),
        videoFeed.followStats(),
      ])
      setFollowingCount(notificationFeedStats.results.following.count)
      setFollowersCount(videoFeedStats.results.followers.count)
    })()

    if (metadata) {
      setInitialMetadata(metadata)
    }

    refetchProfileData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  if (!address) {
    return <></>
  }

  const bio =
    isMyProfile && updatedMetadata?.bio !== undefined ? updatedMetadata?.bio : metadata?.bio
  const name =
    isMyProfile && updatedMetadata?.display_name !== undefined
      ? updatedMetadata.display_name
      : metadata?.display_name || (address && getShortenedAddress(address))

  return (
    <>
      {/* When no banner image: Use gradient which also covers the room 
      // below the header */}
      {!bannerImageExists && (
        <BannerGradientWrapper>
          <GradientBanner seed={address} />
        </BannerGradientWrapper>
      )}
      <BannerShadow />
      {isMyProfile && updatedMetadata?.banner_image_uri ? (
        <BannerImage
          src={
            'https://glassimg.imgix.net/' +
            updatedMetadata?.banner_image_uri +
            `?h=500&crop=fill&fm=webp`
          }
        />
      ) : metadata?.banner_image_uri ? (
        <BannerImage
          src={
            'https://glassimg.imgix.net/' + metadata.banner_image_uri + `?h=500&crop=fill&fm=webp`
          }
        />
      ) : null}
      <Column>
        <ImageSpacer />
        <OuterDiv>
          <ProfileDetailsWrapper>
            <ProfileImageContainer>
              <ProfileImage
                size={isMobile ? 100 : 140}
                item={{
                  displayName: name,
                  profileImageUri,
                  address,
                }}
              />
            </ProfileImageContainer>
            <MainInfo>
              <NameTitle>
                {name
                  ? name
                  : address &&
                    address.substring(0, 4) + '...' + address.substring(address.length - 4)}
              </NameTitle>
              {bio && (
                <Description
                  style={{ marginBottom: showExpand ? '0px' : '5px', marginTop: '5px' }}
                  id="desc"
                  expanded={expanded}
                  showExpand={showExpand}
                  numOfLines={2}
                >
                  {bio.replaceAll('\\n', '\n')}
                </Description>
              )}
              {showExpand && (
                <DescExpand onClick={() => expandDesc(!expanded)}>
                  {expanded ? 'SHOW LESS' : 'SHOW MORE'}
                </DescExpand>
              )}
              {address && (
                <SocialContainer
                  address={address}
                  youtubeHandle={youtubeHandle}
                  twitterHandle={twitterHandle}
                  instagramHandle={instagramHandle}
                />
              )}
            </MainInfo>
            {!isTablet && (
              <StatsDiv>
                <StatDiv>
                  <StatValue>{collectors}</StatValue>
                  <StatTitle>{collectors === 1 ? 'COLLECTOR' : 'COLLECTORS'}</StatTitle>
                </StatDiv>
                <StatDiv>
                  <StatValue>{collectedFrom}</StatValue>
                  <StatTitle>SUPPORTED</StatTitle>
                </StatDiv>
              </StatsDiv>
            )}
          </ProfileDetailsWrapper>
          <FollowDiv>
            <FollowChip
              followingCount={followingCount}
              followersCount={followersCount}
              address={address}
            />
            {isTablet && (
              <StatDiv>
                <StatValue>{collectors}</StatValue>
                <StatTitle>{collectors === 1 ? 'COLLECTOR' : 'COLLECTORS'}</StatTitle>
              </StatDiv>
            )}
            {isTablet && (
              <StatDiv>
                <StatValue>{collectedFrom}</StatValue>
                <StatTitle>SUPPORTED</StatTitle>
              </StatDiv>
            )}
          </FollowDiv>
        </OuterDiv>
      </Column>
      <ProfileEditDialog
        address={address}
        metadata={(isMyProfile && updatedMetadata) || metadata}
      />
      <FollowDialog
        name={name}
        address={address}
        followingCount={followingCount}
        setFollowingCount={setFollowingCount}
        followersCount={followersCount}
      />
    </>
  )
}

export default ProfileHeader
